export interface OnboardingFlags {
  /** Tour id -> highest tour version the user has finished or skipped. */
  seenTours: Record<string, number>;
  /** Hint id -> dismissal timestamp. */
  dismissedHints: Record<string, number>;
  checklistDismissed: boolean;
  checklistCollapsed: boolean;
  updatedAt: number;
}

export const ANONYMOUS_KEY = 'anon';

export const emptyFlags = (): OnboardingFlags => ({
  seenTours: {},
  dismissedHints: {},
  checklistDismissed: false,
  checklistCollapsed: false,
  updatedAt: 0,
});

export const userKey = (userId: string | number | null | undefined): string => {
  const id = userId === null || userId === undefined ? '' : String(userId).trim();
  return id ? `u:${id}` : ANONYMOUS_KEY;
};

export const flagsFor = (users: Record<string, OnboardingFlags>, key: string): OnboardingFlags =>
  users[key] ?? emptyFlags();

export const hasSeenTour = (flags: OnboardingFlags, tourId: string, version = 1): boolean =>
  (flags.seenTours[tourId] ?? 0) >= version;

export const markTourSeen = (
  flags: OnboardingFlags,
  tourId: string,
  version = 1,
  now = Date.now(),
): OnboardingFlags => {
  if (hasSeenTour(flags, tourId, version)) return flags;
  return { ...flags, seenTours: { ...flags.seenTours, [tourId]: version }, updatedAt: now };
};

export const isHintDismissed = (flags: OnboardingFlags, hintId: string): boolean =>
  hintId in flags.dismissedHints;

export const dismissHint = (
  flags: OnboardingFlags,
  hintId: string,
  now = Date.now(),
): OnboardingFlags => {
  if (isHintDismissed(flags, hintId)) return flags;
  return { ...flags, dismissedHints: { ...flags.dismissedHints, [hintId]: now }, updatedAt: now };
};

export const setChecklist = (
  flags: OnboardingFlags,
  patch: Partial<Pick<OnboardingFlags, 'checklistDismissed' | 'checklistCollapsed'>>,
  now = Date.now(),
): OnboardingFlags => ({ ...flags, ...patch, updatedAt: now });

/**
 * Combines two flag sets so that anything seen or dismissed on either side
 * stays seen. Checklist visibility follows whichever side changed last.
 */
export const mergeFlags = (a: OnboardingFlags, b: OnboardingFlags): OnboardingFlags => {
  const seenTours: Record<string, number> = { ...a.seenTours };
  for (const [id, version] of Object.entries(b.seenTours)) {
    seenTours[id] = Math.max(seenTours[id] ?? 0, version);
  }
  const dismissedHints: Record<string, number> = { ...a.dismissedHints };
  for (const [id, at] of Object.entries(b.dismissedHints)) {
    dismissedHints[id] = Math.min(dismissedHints[id] ?? at, at);
  }
  const newer = b.updatedAt > a.updatedAt ? b : a;
  return {
    seenTours,
    dismissedHints,
    checklistDismissed: newer.checklistDismissed,
    checklistCollapsed: newer.checklistCollapsed,
    updatedAt: Math.max(a.updatedAt, b.updatedAt),
  };
};

const isRecordOf = (value: unknown, check: (v: unknown) => boolean): boolean =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.values(value as Record<string, unknown>).every(check);

/** Accepts untrusted JSON (server field or old storage) and returns valid flags. */
export const normalizeFlags = (raw: unknown): OnboardingFlags => {
  const base = emptyFlags();
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Record<string, unknown>;
  const isNum = (v: unknown) => typeof v === 'number' && Number.isFinite(v);
  return {
    seenTours: isRecordOf(r.seenTours ?? r.seen_tours, isNum)
      ? { ...((r.seenTours ?? r.seen_tours) as Record<string, number>) }
      : base.seenTours,
    dismissedHints: isRecordOf(r.dismissedHints ?? r.dismissed_hints, isNum)
      ? { ...((r.dismissedHints ?? r.dismissed_hints) as Record<string, number>) }
      : base.dismissedHints,
    checklistDismissed: Boolean(r.checklistDismissed ?? r.checklist_dismissed),
    checklistCollapsed: Boolean(r.checklistCollapsed ?? r.checklist_collapsed),
    updatedAt: isNum(r.updatedAt ?? r.updated_at) ? Number(r.updatedAt ?? r.updated_at) : 0,
  };
};

export const flagsEqual = (a: OnboardingFlags, b: OnboardingFlags): boolean =>
  JSON.stringify(a) === JSON.stringify(b);
